import dayjs from "dayjs";
import { action, computed, makeObservable, observable } from "mobx";
import toast from "react-hot-toast";
import { DATE_FORMAT, MONTH_YEAR_FORMAT } from "../../constants/constants";
import inventorySalePurchaseEnum from "../../constants/enums/inventory-sale-purchase-enum";
import URLConstants from "../../constants/url-constants";
import { getEffectiveMonthYear } from "../../helpers/date-time-helper";
import { getLanguageFromLocalStorage } from "../../helpers/localstorage-helper";
import { IObservableInitialState } from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import {
  IInventoryFilter,
  IInventoryList,
  IInventoryListVM,
  IInventoryVM,
  IUpdateInventory,
} from "../../models/response/IInventoryManagementResponse";
import { formatMessage } from "../../translations/format-message";
import { InventoryInitialState } from "../initialState/get-all-inventory-state";
import * as baseService from "../service/base-service";

export class InventoryStore {
  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  inventoryList: IInventoryListVM = InventoryInitialState;
  inventory: any = undefined;

  addUpdateInventorytState = { ...this.initialStateValue };
  getAllInventoryListState = { ...this.initialStateValue };
  getInventorytByIdState = { ...this.initialStateValue };
  inventoryFilter: IInventoryFilter = {
    ProductName: "",
    IsAll: false,
    IsSale: false,
    IsPurchase: false,
    MonthYear: getEffectiveMonthYear(),
    isFilterActive: false,
    isFilterChanged: false,
    showClear: false,
  };

  constructor() {
    makeObservable(this, {
      initialStateValue: observable,
      inventoryList: observable,
      inventoryFilter: observable,
      addUpdateInventorytState: observable,
      getAllInventoryListState: observable,
      getInventorytByIdState: observable,
      GetAllInventoryListService: action,
      AddInventoryService: action,
      resetInventoryList: action,
      resetAddUpdateInventory: action,
      setInventoryFilterDetail: action,
      resetInventoryFilterDetail: action,
      getInventoryFilter: computed,
      allInventoryList: computed,
    });
  }

  GetAllInventoryListService = (
    storeId: number,
    orderBy: string,
    column: string,
    filter: any
  ) => {
    this.inventoryList = InventoryInitialState;
    this.getAllInventoryListState.inProgress = true;
    const urlParams = new URLSearchParams();
    let sales: inventorySalePurchaseEnum | null = inventorySalePurchaseEnum.All;
    if (filter.IsAll) sales = inventorySalePurchaseEnum.All;
    else if (filter.IsSale && filter.IsPurchase)
      sales = inventorySalePurchaseEnum.SalePurchase;
    else if (filter.IsSale) sales = inventorySalePurchaseEnum.Sale;
    else if (filter.IsPurchase) sales = inventorySalePurchaseEnum.Purchased;
    else sales = null;
    urlParams.append("orderBy", orderBy);
    urlParams.append("Column", column);
    if (filter.ProductName)
      urlParams.append("productName", String(filter.ProductName ?? ""));
    if(sales !== null)
      urlParams.append("sales", sales?.toString());
    if (filter.MonthYear) {
      const firstDateOfMonth = dayjs(filter.MonthYear, MONTH_YEAR_FORMAT)
        .startOf("month")
        .format(DATE_FORMAT);
      urlParams.append("date", firstDateOfMonth);
    }
    if (storeId > 0) {
      urlParams.append("store_Id", String(storeId));
    }
    const url =
      URLConstants.GetInventoryManagementListByFilter +
      "?" +
      urlParams.toString();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IInventoryListVM>>) => {
        if (response.data.Error) {
          this.getAllInventoryListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.inventoryList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getAllInventoryListState.error = err;
      })
      .finally(
        action(() => {
          this.getAllInventoryListState.inProgress = false;
        })
      );
  };

  AddInventoryService = (data: IUpdateInventory) => {
    this.addUpdateInventorytState.inProgress = true;
    let url = URLConstants.InventoryManagement;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateInventorytState.error = response.data.Message;
        } else this.addUpdateInventorytState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateInventorytState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateInventorytState.inProgress = false;
        })
      );
  };

  GetInventoryByIdService = (id: number) => {
    this.getInventorytByIdState.inProgress = true;
    let url = `${URLConstants.GetInventoryById}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getInventorytByIdState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.inventory = data;
        }
      })
      .catch((err: string) => {
        this.getInventorytByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getInventorytByIdState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to map inventoryList suitable for Grid Component.
   * @returns allInventoryList
   */
  get allInventoryList(): IInventoryList[] {
    if (
      this.inventoryList?.Inventory &&
      this.inventoryList.Inventory?.length > 0
    ) {
      const data = this.inventoryList.Inventory.map(
        (inventoryVM: IInventoryVM, index: number) => {
          let isDelete = true;
          let isEdit = true;
          let unitPriceExcludingTax = inventoryVM.UnitPriceExcludingTax || 0;
          let priceExcludingTax = inventoryVM.PriceExcludingTax || 0;
          let endingInventoryQuantity =
            inventoryVM.CarryoverQuantity +
            inventoryVM.IncomingQuantity +
            inventoryVM.AdjustedQuantity -
            (inventoryVM.SalesQuantity + inventoryVM.InternalUsedQuantity);
          let language = getLanguageFromLocalStorage();
          return {
            Id: index + 1,
            StoreId: inventoryVM.StoreId,
            ProductId: inventoryVM.ProductId,
            Barcode: inventoryVM.Barcode,
            ProductName: inventoryVM.ProductName,
            UnitPriceExcludingTax: unitPriceExcludingTax,
            CarryoverQuantity: inventoryVM.CarryoverQuantity || 0,
            CarryoverAmount:
              (inventoryVM.CarryoverQuantity || 0) * unitPriceExcludingTax,
            IncomingQuantity: inventoryVM.IncomingQuantity || 0,
            IncomingAmount:
              (inventoryVM.IncomingQuantity || 0) * unitPriceExcludingTax,
            PriceExcludingTax: priceExcludingTax,
            SalesQuantity: inventoryVM.SalesQuantity || 0,
            SalesAmount: (inventoryVM.SalesQuantity || 0) * priceExcludingTax,
            CostAmount:
              (inventoryVM.SalesQuantity || 0) * unitPriceExcludingTax,
            InternalUsedQuantity: inventoryVM.InternalUsedQuantity || 0,
            InternalUsedAmount:
              (inventoryVM.InternalUsedQuantity || 0) * unitPriceExcludingTax,
            EndingInventoryQuantity: endingInventoryQuantity || 0,
            EndingInventoryAmount:
              (endingInventoryQuantity || 0) * unitPriceExcludingTax,
            AdjustedQuantity: inventoryVM.AdjustedQuantity || 0,
            AdjustedAmount:
              (inventoryVM.AdjustedQuantity || 0) * unitPriceExcludingTax,
            TransactionDate: inventoryVM.TransactionDate,
            Notes: inventoryVM.Notes,
            UserId: inventoryVM.UserId,
            isDelete: isDelete,
            isEdit: isEdit,
            language: language,
          };
        }
      );
      const rawList = data;

      const sum = (field: keyof IInventoryList): number =>
        rawList.reduce(
          (acc, curr) => acc + (curr[field] ? Number(curr[field]) : 0),
          0
        );
      const lastVM = rawList[rawList.length - 1];

      const totalRow: IInventoryList = {
        Id: data.length + 1,
        StoreId: lastVM.StoreId,
        ProductId: data.length + 1,
        Barcode: "",
        ProductName: formatMessage("total"),
        UnitPriceExcludingTax: sum("UnitPriceExcludingTax"),
        CarryoverQuantity: sum("CarryoverQuantity"),
        CarryoverAmount: sum("CarryoverAmount"),
        IncomingQuantity: sum("IncomingQuantity"),
        IncomingAmount: sum("IncomingAmount"),
        PriceExcludingTax: sum("PriceExcludingTax"),
        SalesQuantity: sum("SalesQuantity"),
        SalesAmount: sum("SalesAmount"),
        CostAmount: sum("CostAmount"),
        InternalUsedQuantity: sum("InternalUsedQuantity"),
        InternalUsedAmount: sum("InternalUsedAmount"),
        EndingInventoryQuantity: sum("EndingInventoryQuantity"),
        EndingInventoryAmount: sum("EndingInventoryAmount"),
        AdjustedQuantity: sum("AdjustedQuantity"),
        AdjustedAmount: sum("AdjustedAmount"),
        TransactionDate: "",
        Notes: "",
        UserId: lastVM.UserId,
        isEdit: false,
      };

      return [...data, totalRow];
    }
    return [];
  }

  resetInventoryList = () => {
    this.getAllInventoryListState = { ...this.initialStateValue };
    this.inventoryList = InventoryInitialState;
  };

  resetAddUpdateInventory = () => {
    this.addUpdateInventorytState = { ...this.initialStateValue };
  };

  setInventoryFilterDetail = (filter: IInventoryFilter) => {
    this.inventoryFilter = { ...filter };
  };

  resetInventoryFilterDetail = () => {
    this.inventoryFilter = {
      ProductName: "",
      IsAll: false,
      IsSale: false,
      IsPurchase: false,
      MonthYear: getEffectiveMonthYear(),
      isFilterActive: false,
      isFilterChanged: false,
      showClear: false,
    };
  };

  get getInventoryFilter(): IInventoryFilter {
    return this.inventoryFilter;
  }
}

export default new InventoryStore();
